// services/childcare/auth.js
import {
  api,
  getStoredUser,
  isChildcareLoggedIn,
  childcareLogout,
  getFacilityId,
  hasRole,
  hasChildcareAccess,
} from "./utils";

// Childcare Login
export const childcareLogin = async (email, password) => {
  try {
    const response = await api.post("/childcare/login", { email, password });
    if (response.data?.success) {
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
      }
      if (response.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        if (response.data.user.facilityId) {
          localStorage.setItem("facilityId", response.data.user.facilityId);
        }
      }
    }
    return response;
  } catch (error) {
    console.error(
      "Login error:",
      error.response?.data?.message || error.message,
    );
    throw error;
  }
};

// Social Login (Google / Apple)
export const childcareSocialLogin = async (idToken, provider) => {
  try {
    const response = await api.post(`/childcare/social/${provider}`, { idToken });
    if (response.data?.success && response.data.token) {
      localStorage.setItem("token", response.data.token);
      if (response.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        if (response.data.user.facilityId) {
          localStorage.setItem("facilityId", String(response.data.user.facilityId));
        }
      }
    }
    return response;
  } catch (error) {
    console.error("Social login error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Childcare Forgot Password
export const childcareForgotPassword = async (email) => {
  try {
    const response = await api.post("/childcare/forgot-password", { email });
    return response;
  } catch (error) {
    console.error(
      "Forgot password error:",
      error.response?.data?.message || error.message,
    );
    throw error;
  }
};

// Childcare Verify OTP
export const childcareVerifyOTP = async (email, otp) => {
  try {
    const response = await api.post("/childcare/verify-otp", { email, otp });
    return response;
  } catch (error) {
    console.error(
      "Verify OTP error:",
      error.response?.data?.message || error.message,
    );
    throw error;
  }
};

// Childcare Reset Password
export const childcareResetPassword = async (email, otp, newPassword) => {
  try {
    const response = await api.post("/childcare/reset-password", {
      email,
      otp,
      newPassword,
    });
    return response;
  } catch (error) {
    console.error(
      "Reset password error:",
      error.response?.data?.message || error.message,
    );
    throw error;
  }
};

// Childcare Change Password
export const childcareChangePassword = async (currentPassword, newPassword) => {
  try {
    const response = await api.post("/childcare/change-password", {
      currentPassword,
      newPassword,
    });
    return response;
  } catch (error) {
    console.error(
      "Change password error:",
      error.response?.data?.message || error.message,
    );
    throw error;
  }
};

// Get Current User
export const getCurrentUser = async () => {
  try {
    const response = await api.get("/childcare/me");
    if (response.data?.success) {
      localStorage.setItem("user", JSON.stringify(response.data.user));
      return response.data.user;
    }
    return null;
  } catch (error) {
    console.error("Failed to get current user:", error);
    return null;
  }
};

// Delete own account (admin: soft-delete; co-admin/staff: returns 400 — use requestDeletion)
export const deleteChildcareAccount = async () => {
  try {
    const response = await api.delete("/childcare/delete-account");
    return response;
  } catch (error) {
    console.error("Delete account error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// ==================== ACCOUNT DELETION WORKFLOW ====================

// Co-admin/Staff: submit a deletion request
export const requestDeletion = async (reason = "") => {
  try {
    const response = await api.post("/childcare/account/request-delete", { reason });
    return response;
  } catch (error) {
    console.error("Request deletion error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Co-admin/Staff: check own deletion request status
export const getDeletionStatus = async () => {
  try {
    const response = await api.get("/childcare/account/deletion-status");
    return response;
  } catch (error) {
    console.error("Get deletion status error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Co-admin/Staff: cancel pending deletion request
export const cancelDeletionRequest = async () => {
  try {
    const response = await api.post("/childcare/account/cancel-deletion");
    return response;
  } catch (error) {
    console.error("Cancel deletion error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Admin: soft-delete a co-admin or staff member
export const adminDeleteUser = async (userId) => {
  try {
    const response = await api.delete(`/childcare/admin/users/${userId}`);
    return response;
  } catch (error) {
    console.error("Admin delete user error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Admin: restore a soft-deleted user (within 7-day window)
export const adminRestoreUser = async (userId) => {
  try {
    const response = await api.post(`/childcare/admin/restore/${userId}`);
    return response;
  } catch (error) {
    console.error("Restore user error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Admin: get all soft-deleted users for the facility
export const getDeletedUsers = async () => {
  try {
    const response = await api.get("/childcare/admin/deleted-users");
    return response;
  } catch (error) {
    console.error("Get deleted users error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Admin: get deletion requests (filter by status: pending | approved | rejected)
export const getDeletionRequests = async (status = "pending") => {
  try {
    const response = await api.get("/childcare/admin/deletion-requests", {
      params: { status },
    });
    return response;
  } catch (error) {
    console.error("Get deletion requests error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Admin: approve or reject a deletion request
export const handleDeletionRequest = async (requestId, action, rejectionReason = "") => {
  try {
    const response = await api.put(`/childcare/admin/deletion-requests/${requestId}`, {
      action,
      rejection_reason: rejectionReason,
    });
    return response;
  } catch (error) {
    console.error("Handle deletion request error:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Export helper functions
export {
  getStoredUser,
  isChildcareLoggedIn,
  childcareLogout,
  getFacilityId,
  hasRole,
  hasChildcareAccess,
};
